import type { AutoCompleteMenu } from '../../components/autocomplete.menu/autocomplete.menu';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import type { CustomMithrilVNode, MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Logger, Services } from '../../services';
import { Highlighter } from '../../services/util/highlighter';
import { KeyCode } from '../../services/util/key.binding';
import type { TagNameInputController } from './tag.name.input.ctrl';
import { CSS, Style } from './tag.name.input.style';

addStyles(Style);

export interface TagNameInputAttrs {
	ctrl: TagNameInputController;
}

export const TagNameReadonlyInput = {
	view(ctrl: TagNameInputController): MithrilVNode {
		const tag = ctrl.tag;

		var attributes = {
			className: CSS.link,
			onclick: (_) => ctrl.zoomToTag(),
		};
		const timeString = Services.DateTime.formatDateTime(tag.updatedAt);

		var tagNameChildren: string | MithrilVNode[] = tag.name;
		const filterValue = ctrl.getTagFilterValue();
		if (filterValue != null && filterValue.length > 0) {
			tagNameChildren = Highlighter.highlight(tag.name, filterValue).parts.map((part) =>
				m('span', { className: part.highlighted ? CSS.filterMatch : undefined }, part.content)
			);
		}

		return m('div', attributes, [
			m('span', { className: CSS.title, title: tag.name }, tagNameChildren),
			m('div', { className: CSS.date, title: Translate(T.LAST_UPDATED_AT, { timeString }) }, timeString),
		]);
	},
};

export const TagNameInput = {
	view(ctrl: TagNameInputController, autoCompleteMenu: AutoCompleteMenu): MithrilVNode {
		const KeyEvents = {
			[KeyCode.ESCAPE]: () => ctrl.cancelEdit(),
			[KeyCode.ENTER]: () => ctrl.acceptEdit(),
		};

		const tagNameChildren = m('input', {
			className: CSS.linkEditable,
			spellcheck: false,
			value: ctrl.getInputValue(),
			onkeyup: KeyCode.bind(KeyEvents),
			oninput: (e: Event) => ctrl.updateEdit((e.target as HTMLInputElement).value),
			oncreate: (vnode) => TagNameInput.startEdit(vnode, ctrl),
			onfocus: () => ctrl.getInputState().setFocused(true),
			onblur: () => ctrl.getInputState().setFocused(false),
		});

		const acceptButton = ImageButton.view(MaterialIcon.CHECK, {
			type: 'button',
			onclick: (e: MouseEvent) => {
				e.stopPropagation();
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				ctrl.acceptEdit();
			},
		});

		const cancelButton = ImageButton.view(MaterialIcon.CLOSE, {
			type: 'button',
			onclick: (e: MouseEvent) => {
				e.stopPropagation();
				ctrl.cancelEdit();
			},
		});

		return m('div', { className: CSS.container }, [
			tagNameChildren,
			autoCompleteMenu.view(),
			acceptButton,
			cancelButton,
		]);
	},

	startEdit(vnode: CustomMithrilVNode<TagNameInputAttrs>, ctrl: TagNameInputController) {
		const element = vnode.dom as HTMLInputElement;
		const tagCard = element.parentElement?.parentElement;
		if (!tagCard) {
			Logger.error(`Cannot find Tag Card of ${element}`);
			return;
		}

		const handleClickAway = () => {
			ctrl.tagCard = tagCard;
			ctrl.cancelEdit();
		};

		Services.FocusAway.from(tagCard, handleClickAway);

		element.focus();
		element.select();
	},
};
